import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Minus, ShoppingCart, MessageCircle, ShieldCheck, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { PRODUCTS as _seed, CATEGORIES, BIGTIME_WHATSAPP } from '@/mock/products';
import { useAvailableProducts, useSelectedStore, useCategories } from '@/store/dataStore';
import ProductCard from '@/components/ProductCard';
import ProductReviews from '@/components/ProductReviews';
import { useSEO } from '@/hooks/useSEO';
import { useSettings } from '@/services/settingsService';
import ProductNotes from '@/components/ProductNotes';
import { thumb } from '@/lib/img';

export default function ProductDetailPage() {
  const { products: PRODUCTS } = useAvailableProducts();
  const { store } = useSelectedStore();
  const { categories } = useCategories();
  const { settings } = useSettings();
  const CATS = categories && categories.length ? categories : CATEGORIES;
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null); // { name, hex, image }
  const [selectedSize, setSelectedSize] = useState(null);   // string
  const [zoomed, setZoomed] = useState(false);

  const product = PRODUCTS.find((p) => p.id === id);

  useSEO(product ? {
    title: `${product.name} · $${product.price.toFixed(2)} al mayor · BIGTIME Venezuela`,
    description: `${product.name} desde 1 pieza a precio mayorista. ${product.description || 'Envío MRW/Zoom a toda Venezuela, garantía con video. Mejor precio que Alibaba.'}`,
    keywords: `${product.name} al mayor, ${product.category || ''} mayorista Venezuela, precios mejores que Alibaba, ventas por mayor Caracas Maracaibo`,
    image: product.image,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: (product.description || '').slice(0, 500),
      image: [product.image, ...(product.images || [])].filter(Boolean),
      sku: product.internal_code || product.id,
      brand: { '@type': 'Brand', name: 'BIGTIME' },
      category: product.category,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'USD',
        price: product.price.toFixed(2),
        availability: 'https://schema.org/InStock',
        url: (typeof window !== 'undefined' ? window.location.href : ''),
      },
    },
  } : { title: 'Producto · BIGTIME' });

  useEffect(() => {
    window.scrollTo(0, 0);
    setQty(1);
    setSelectedColor(null);
    setSelectedSize(null);
  }, [id]);

  useEffect(() => {
    if (!product) return undefined;
    document.body.classList.add('has-sticky-cta');
    return () => document.body.classList.remove('has-sticky-cta');
  }, [product]);

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <p className="text-slate-600">Producto no encontrado.</p>
        <Link to="/catalogo" className="text-[#1B2A4A] font-semibold underline mt-3 inline-block">Volver al catálogo</Link>
      </div>
    );
  }

  const cat = CATS.find((c) => c.id === product.category);
  const related = PRODUCTS.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 5);
  const colors = product.variants?.colors || [];
  const sizes = product.variants?.sizes || [];

  // Build the gallery: main image + product.images[] + variant images.
  // Dedup so the same URL doesn't appear twice. Simple loop — cheap enough
  // to run every render.
  const gallery = (() => {
    const seen = new Set();
    const list = [];
    const push = (u) => { if (u && !seen.has(u)) { seen.add(u); list.push(u); } };
    push(product.image);
    for (const u of (product.images || [])) push(u);
    for (const c of colors) push(c?.image);
    return list;
  })();

  const activeImage = selectedColor?.image || product.image || gallery[0] || '';

  // Variant-aware effective price + code.
  // Precedence: sizeVariant > colorVariant > product base.
  const selectedSizeObj = typeof selectedSize === 'object'
    ? selectedSize
    : (sizes.find((s) => (typeof s === 'string' ? s : s?.name) === selectedSize) || null);
  const effectivePrice =
    (selectedSizeObj?.price ?? undefined) ??
    (selectedColor?.price ?? undefined) ??
    product.price;
  const effectiveCode =
    (selectedSizeObj?.internal_code || '') ||
    (selectedColor?.internal_code || '') ||
    (product.internal_code || '');

  const addToCart = () => addItem({ ...product, price: effectivePrice, internal_code: effectiveCode || product.internal_code }, qty, { color: selectedColor, size: selectedSizeObj || selectedSize });

  const buyNow = () => {
    const sizeName = typeof selectedSize === 'object' ? selectedSize?.name : selectedSize;
    const variantLine = [
      selectedColor?.name ? `Color: ${selectedColor.name}` : null,
      sizeName ? `Talla: ${sizeName}` : null,
      effectiveCode ? `Código: ${effectiveCode}` : null,
    ].filter(Boolean).join(' | ');
    const parts = [
      'Hola BIGTIME, me interesa este producto:',
      '',
      `*${product.name}*`,
      variantLine || null,
      `Cantidad: ${qty}`,
      `Precio: $${(effectivePrice * qty).toFixed(2)}`,
      '',
      '¿Sigue disponible?',
    ].filter((l) => l !== null);
    const msg = encodeURIComponent(parts.join('\n'));
    const target = store?.whatsapp || BIGTIME_WHATSAPP;
    window.open(`https://api.whatsapp.com/send?phone=${target}&text=${msg}`, '_blank');
  };

  return (
    <div className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#1B2A4A] mb-4">
        <ArrowLeft size={16} /> Volver
      </button>

      <div className="grid md:grid-cols-2 gap-5 md:gap-8">
        <div>
          <div
            className="bg-slate-50 rounded-2xl overflow-hidden aspect-square group cursor-zoom-in relative"
            data-testid="pdp-main-image-wrap"
            onMouseLeave={() => setZoomed(false)}
            onMouseMove={(e) => {
              if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
              const el = e.currentTarget.querySelector('img'); if (!el) return;
              if (!zoomed) setZoomed(true);
              const rect = e.currentTarget.getBoundingClientRect();
              const x = ((e.clientX - rect.left) / rect.width) * 100;
              const y = ((e.clientY - rect.top) / rect.height) * 100;
              el.style.transformOrigin = `${x}% ${y}%`;
            }}
            onClick={(e) => {
              // Tap-to-fullscreen zoom on mobile / any device
              const src = e.currentTarget.querySelector('img')?.src;
              if (!src) return;
              setZoomed(false);
              const overlay = document.createElement('div');
              overlay.setAttribute('data-testid', 'pdp-zoom-overlay');
              overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.92);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;cursor:zoom-out';
              const im = document.createElement('img'); im.src = src; im.style.cssText = 'max-width:100%;max-height:100%;object-fit:contain;border-radius:12px';
              overlay.appendChild(im); overlay.addEventListener('click', () => overlay.remove());
              document.body.appendChild(overlay);
            }}
          >
            <img
              src={activeImage}
              alt={product.name}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className={`block w-full h-full max-w-none object-contain transition-transform duration-300 ${zoomed ? 'scale-[1.6]' : 'scale-100'}`}
              data-testid="pdp-main-image"
              key={activeImage}
            />
            <span className={`absolute bottom-2 right-2 text-[10px] bg-white/90 text-[#1B2A4A] font-bold px-2 py-1 rounded-full pointer-events-none transition ${zoomed ? 'opacity-100' : 'opacity-0'}`} data-testid="pdp-zoom-hint">🔍 Zoom</span>
          </div>

          {/* Thumbnail carousel — shows main image + product.images[] + variant photos */}
          {gallery.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1 snap-x snap-mandatory" data-testid="pdp-thumbnails">
              {gallery.map((url, i) => (
                <button
                  key={url + i}
                  type="button"
                  onClick={() => {
                    // Setting selectedColor drives activeImage; if this url matches a color use it,
                    // otherwise clear color and rely on the fact activeImage falls back to product.image.
                    const matchColor = colors.find((c) => c?.image === url);
                    if (matchColor) setSelectedColor(matchColor);
                    else setSelectedColor(url === product.image ? null : { name: '', hex: '', image: url });
                  }}
                  className={`snap-start flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition ${activeImage === url ? 'border-[#1B2A4A] ring-2 ring-yellow-300' : 'border-slate-200 hover:border-slate-400'}`}
                  data-testid={`pdp-thumb-${i}`}
                  aria-label={`Foto ${i + 1} de ${gallery.length}`}
                >
                  <img src={thumb(url, 200)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {cat && <Link to={`/catalogo?cat=${cat.id}`} className="text-xs uppercase tracking-wider text-slate-500 hover:text-[#1B2A4A] font-semibold">{cat.name}</Link>}
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#1B2A4A] mt-1">{product.name}</h1>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="text-4xl font-extrabold text-[#1B2A4A]" data-testid="pdp-price">${effectivePrice.toFixed(2)}</span>
            <span className="text-sm text-slate-500">USD</span>
            {(selectedColor?.price != null || selectedSizeObj?.price != null) && (
              <span className="text-xs bg-yellow-100 text-[#1B2A4A] font-bold rounded-full px-2 py-0.5" data-testid="pdp-variant-price-badge">Precio de variante</span>
            )}
          </div>
          {(() => {
            const pct = Math.max(0, parseFloat(settings?.usd_discount_percent) || 0);
            if (pct <= 0) return null;
            const divisas = effectivePrice * (1 - pct / 100);
            return (
              <div className="mt-1 flex items-center gap-2 text-sm" data-testid="pdp-price-divisas">
                <span className="bg-yellow-100 text-[#1B2A4A] font-bold rounded px-2 py-0.5 text-xs">Precio Divisas</span>
                <span className="text-green-600 font-extrabold text-xl">${divisas.toFixed(2)}</span>
                <span className="text-xs text-slate-400">pagando en efectivo USD (−{pct}%)</span>
              </div>
            );
          })()}
          {effectiveCode && (
            <p className="mt-1 text-xs text-slate-500" data-testid="pdp-variant-code">Código: <span className="font-mono">{effectiveCode}</span></p>
          )}

          <p className="mt-5 text-slate-600 leading-relaxed">{product.description}</p>

          {colors.length > 0 && (
            <div className="mt-6" data-testid="pdp-colors">
              <span className="text-sm font-semibold text-slate-700 block mb-2">
                Color{selectedColor ? `: ${selectedColor.name}` : ''}
              </span>
              <div className="flex flex-wrap gap-2">
                {colors.map((c) => {
                  const cname = c.name || c;
                  const active = selectedColor && (selectedColor.name || selectedColor) === cname;
                  return (
                    <button
                      key={cname}
                      type="button"
                      onClick={() => setSelectedColor(c)}
                      title={cname}
                      data-testid={`pdp-color-${cname}`}
                      className={`w-10 h-10 rounded-full border-2 transition ${active ? 'border-[#1B2A4A] ring-2 ring-yellow-300 scale-110' : 'border-slate-300 hover:border-[#1B2A4A]'}`}
                      style={{ background: c.hex || '#000' }}
                    />
                  );
                })}
                {selectedColor && (
                  <button
                    type="button"
                    onClick={() => setSelectedColor(null)}
                    className="text-xs text-slate-500 hover:text-[#1B2A4A] underline ml-2"
                    data-testid="pdp-color-clear"
                  >
                    limpiar
                  </button>
                )}
              </div>
            </div>
          )}

          {sizes.length > 0 && (
            <div className="mt-4" data-testid="pdp-sizes">
              <span className="text-sm font-semibold text-slate-700 block mb-2">
                Talla{selectedSize ? `: ${selectedSize}` : ''}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {sizes.map((s) => {
                  const sname = typeof s === 'string' ? s : s?.name;
                  const currentSizeName = typeof selectedSize === 'object' ? selectedSize?.name : selectedSize;
                  const active = currentSizeName === sname;
                  return (
                    <button
                      key={sname}
                      type="button"
                      onClick={() => setSelectedSize(active ? null : s)}
                      data-testid={`pdp-size-${sname}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition ${active ? 'bg-[#1B2A4A] text-yellow-300 border-[#1B2A4A]' : 'bg-slate-100 border-slate-200 hover:border-[#1B2A4A]'}`}
                    >
                      {sname}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(colors.length > 0 && !selectedColor) || (sizes.length > 0 && !selectedSize) ? (
            <p className="mt-3 text-[11px] text-slate-500" data-testid="pdp-variant-hint">
              💡 {colors.length && !selectedColor ? 'Elige un color' : ''}
              {colors.length && !selectedColor && sizes.length && !selectedSize ? ' y una talla' : (sizes.length && !selectedSize ? 'Elige una talla' : '')}
              {' '}para incluir la variante en tu pedido.
            </p>
          ) : null}

          <div className="mt-6 flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-700">Cantidad:</span>
            <div className="flex items-center border border-slate-300 rounded-full overflow-hidden">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3.5 py-3 hover:bg-slate-100" aria-label="Menos" data-testid="pdp-qty-minus"><Minus size={16} /></button>
              <span className="px-4 font-semibold min-w-[40px] text-center" data-testid="pdp-qty">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3.5 py-3 hover:bg-slate-100" aria-label="Más" data-testid="pdp-qty-plus"><Plus size={16} /></button>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button disabled={!!product.sold_out} onClick={addToCart} className="flex-1 bg-[#F8D13F] hover:bg-yellow-300 text-[#1B2A4A] font-bold h-12 rounded-full" data-testid="pdp-add-to-cart">
              <ShoppingCart size={18} className="mr-2" /> {product.sold_out ? 'Agotado' : 'Agregar al carrito'}
            </Button>
            <Button disabled={!!product.sold_out} onClick={buyNow} className="flex-1 bg-[#25D366] hover:bg-[#1FBA57] text-white font-bold h-12 rounded-full" data-testid="pdp-buy-now">
              <MessageCircle size={18} className="mr-2" /> Comprar por WhatsApp
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
              <Truck size={18} className="text-[#1B2A4A]" /> Envío a todo Venezuela
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
              <ShieldCheck size={18} className="text-[#1B2A4A]" /> Compra garantizada
            </div>
          </div>
        </div>
      </div>

      <ProductNotes productId={product.id} />

      <ProductReviews productId={product.id} title="Opiniones sobre este producto" />

      {related.length > 0 && (
        <div className="mt-16">
          <h2 className="text-2xl font-extrabold text-[#1B2A4A] mb-4">También te puede interesar</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
      <div className="md:hidden fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-2.5 flex items-center gap-3 shadow-[0_-6px_20px_rgba(27,42,74,0.08)]" data-testid="pdp-sticky-bar">
        <div className="min-w-0">
          <p className="text-[11px] text-slate-500 truncate">{product.name}</p>
          <p className="text-lg font-extrabold text-[#1B2A4A] leading-tight" data-testid="pdp-sticky-price">${(effectivePrice * qty).toFixed(2)}{qty > 1 && <span className="text-xs font-semibold text-slate-500"> · {qty} u.</span>}</p>
        </div>
        <Button disabled={!!product.sold_out} onClick={addToCart} className="ml-auto bg-[#F8D13F] hover:bg-yellow-300 text-[#1B2A4A] font-bold h-12 px-5 rounded-full" data-testid="pdp-sticky-add">
          <ShoppingCart size={18} className="mr-2" /> {product.sold_out ? 'Agotado' : 'Agregar'}
        </Button>
      </div>
    </div>
  );
}
